import { exactInteger } from '@/lib/exact'
import type { Block, Cell, ChipCell, Column, KvItem, TableBlock, Tone } from '@/kova/spec/types'

import type { ApiClient } from '@/lib/api'
import { ApiError } from '@/lib/api/errors'
import { exactMinorBig, isCurrencyCode } from '@/lib/money/money'

import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Reporting — all eight screens, read from `/v1/admin/reports/*`
 * (handler `internal/server/admin_reports.go`).
 *
 * # Six of the eight have no figures, and that answer is the product
 *
 * The backend registers the whole section at once and says why:
 *
 *   > a screen without its source serves the honest answer — not_built, with the
 *   > issue that will build it — and that answer is the product. Registering
 *   > only the screens whose data exists would 404 six of eight on every
 *   > deployment, and a console cannot tell a 404 that means "never built" from
 *   > one that means "you are on an old backend".
 *
 * So this console wires all eight, and six of them render a stated absence
 * naming the issue that will fill them. A section that showed two screens and
 * hid six would be a console claiming this is all the reporting there is.
 *
 * # `availability` is never a zero, and never derived from the mirror
 *
 * Three states. `not_built` is a figure nobody has computed; `unavailable` is a
 * source that exists and could not be read; and both differ from a period that
 * genuinely had no activity. Critically it is NOT derived from the mirror's
 * watermark — a readable mirror says the SOURCE is there and says nothing about
 * whether anybody built the aggregate on top of it. A screen that went
 * `available` on a watermark would render green with no rows, which is "not
 * computed" made indistinguishable from "nothing happened".
 *
 * # Law 12 — analytics is badged, and the badge is read from the payload
 *
 * `provenance.source` is `analytics` or `ledger` and the console's source badge
 * reads that field and nothing else. Six of these screens are analytics-sourced
 * and are badged as such with their freshness; two are ledger-sourced on
 * purpose — a tie-out sourced from the thing it is checking is circular, and
 * export state is operational state an operator acts on.
 *
 * # Law 20 — a lagging mirror is degraded, not down
 *
 * `posture` is `fresh | degraded | unavailable`. A mirror behind its budget
 * still serves its figures with the notice; it is never an outage and never a
 * reason to block a money screen. And a figure nothing has tied out is degraded
 * even when it is a minute old — freshness is when it was read, not whether it
 * is right.
 *
 * # `materialized_through` is null rather than now()
 *
 * A stale mirror quoting the moment it was asked is a mirror claiming to be
 * current. `lag_seconds` is absent rather than zero for the same reason: a
 * source that cannot say how current it is has not said it is current.
 *
 * # Law 8 and 9 — every amount carries its currency, and none is a float
 *
 * Report lines carry `Money` — minor units as an exact integer with an explicit
 * ISO-4217 code. They go through `@/lib/money` like every other amount in this
 * console; a figure whose minor units are not exactly representable renders as
 * unplaceable rather than as a rounded number on a screen somebody reconciles
 * against.
 */

// ---------------------------------------------------------------------------
// The wire shape — one envelope for all eight
// ---------------------------------------------------------------------------

export type Availability = 'available' | 'not_built' | 'unavailable'

const AVAILABILITIES: readonly string[] = ['available', 'not_built', 'unavailable']

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function whole(value: unknown): number | null {
  return exactInteger(value)
}

function availabilityOf(value: unknown): Availability | null {
  const a = text(value)
  return AVAILABILITIES.includes(a) ? (a as Availability) : null
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * An amount, placed or refused.
 *
 * Same posture as the ledger screens: a figure that cannot be rendered exactly
 * is not rendered approximately. This is a reporting screen and the numbers on
 * it are quoted at people.
 */
interface Placed {
  readonly ok: boolean
  readonly minor: number | null
  readonly currency: string
  readonly reason: string
}

function place(raw: unknown): Placed {
  if (!isObject(raw)) {
    return { ok: false, minor: null, currency: '', reason: 'no amount was sent' }
  }
  const currency = text(raw['currency'])
  if (!isCurrencyCode(currency)) {
    // Law 8. An amount with no currency is not a number this console will draw:
    // a bare figure beside another bare figure invites a comparison nobody
    // checked the units of.
    return { ok: false, minor: null, currency: '', reason: 'no ISO-4217 currency accompanied the amount' }
  }
  const minor = exactMinorBig(raw['amount_minor'])
  if (minor === null) {
    return { ok: false, minor: null, currency, reason: 'the minor units did not read as an exact integer' }
  }
  if (minor > BigInt(Number.MAX_SAFE_INTEGER) || minor < -BigInt(Number.MAX_SAFE_INTEGER)) {
    return { ok: false, minor: null, currency, reason: 'the amount is beyond the exact integer range of an amount cell' }
  }
  return { ok: true, minor: Number(minor), currency, reason: '' }
}

function amountCell(p: Placed): Cell {
  if (!p.ok) return `— ${p.reason}`
  return [p.minor ?? 0, p.currency]
}

interface Provenance {
  readonly source: string
  readonly dataset: string
  readonly readAt: string
  readonly materializedThrough: string
  readonly lagSeconds: number | null
  readonly posture: string
  readonly tieOut: string
  readonly reason: string
}

function readProvenance(raw: unknown): Provenance | null {
  if (!isObject(raw)) return null
  return {
    source: text(raw['source']),
    dataset: text(raw['dataset']),
    readAt: text(raw['read_at']),
    // NULL, never now(): a stale mirror quoting the moment it was asked is a
    // mirror claiming to be current.
    materializedThrough: text(raw['materialized_through']),
    // ABSENT, never zero, when there is no watermark.
    lagSeconds: whole(raw['lag_seconds']),
    posture: text(raw['posture']),
    tieOut: typeof raw['tie_out'] === 'string' ? raw['tie_out'] : JSON.stringify(raw['tie_out'] ?? null),
    reason: text(raw['reason']),
  }
}

interface ReportLine {
  readonly report: string
  readonly state: string
  readonly kind: string
  readonly periodFrom: string
  readonly periodTo: string
  readonly ledger: Placed | null
  readonly mirror: Placed | null
  readonly difference: Placed | null
}

function readLine(raw: unknown): ReportLine | null {
  if (!isObject(raw)) return null
  const report = text(raw['report'])
  if (report === '') return null
  return {
    report,
    state: text(raw['state']),
    kind: text(raw['kind']),
    periodFrom: text(raw['period_from']),
    periodTo: text(raw['period_to']),
    // Absent rather than zero on `mirror_missing`: a mirror that has never
    // heard of a class has not measured it at nothing, and a zero sends an
    // operator looking for a wrong figure instead of a missing feed.
    ledger: raw['ledger'] === undefined ? null : place(raw['ledger']),
    mirror: raw['mirror'] === undefined ? null : place(raw['mirror']),
    difference: raw['difference'] === undefined ? null : place(raw['difference']),
  }
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

function lineStateChip(l: ReportLine): ChipCell {
  if (l.state === 'tied') return ['tied', 'g']
  if (l.state === 'out_by') return ['OUT BY', 'r']
  if (l.state === 'stale') return ['stale', 'a']
  if (l.state === 'never_ran') return ['never ran', 'a']
  return [l.state === '' ? '— no state' : `unknown: ${l.state}`, 'r']
}

const LINE_COLS: readonly Column[] = [
  ['Bucket', 'm', 'minmax(180px,1.3fr)'],
  ['Period', 'm', '190px'],
  ['Ledger', 'a', '150px'],
  ['Mirror', 'a', '150px'],
  ['Difference', 'a', '150px'],
  ['State', 'c', '140px'],
  ['How they differ', 'x', 'minmax(170px,1.2fr)'],
]

function period(l: ReportLine): string {
  if (l.periodFrom === '' && l.periodTo === '') return '— not stated'
  return `${l.periodFrom.slice(0, 10)} → ${l.periodTo.slice(0, 10)}`
}

function lineRow(l: ReportLine): readonly Cell[] {
  return [
    l.report,
    period(l),
    // A side the backend did not send is ABSENT, never zero.
    l.ledger === null ? '— no figure' : amountCell(l.ledger),
    l.mirror === null ? '— no row on that side' : amountCell(l.mirror),
    l.difference === null ? '—' : amountCell(l.difference),
    lineStateChip(l),
    l.kind === '' ? '—' : l.kind,
  ]
}

function absenceNote(screen: string, title: string, availability: Availability | null, reason: string): Block {
  if (availability === 'not_built') {
    return {
      t: 'note',
      tone: 'n',
      kicker: 'Not built yet',
      title: `${title} has no figures, and nobody has computed them`,
      body: `${reason === '' ? 'The backend did not say why.' : reason} This is a roadmap fact rather than an incident: nothing is broken, and no operator action makes a figure appear. It is stated rather than drawn as an empty table, because an empty table under a green heading reads as "nothing happened" — which is a claim about the business rather than about what has been built.`,
      ref: `${screen} · availability = not_built`,
    }
  }
  if (availability === 'unavailable') {
    return {
      t: 'note',
      tone: 'r',
      kicker: 'Could not be read',
      title: `${title}'s source exists and did not answer`,
      body: `${reason === '' ? 'The backend did not say why.' : reason} This is an incident rather than a gap — the difference from "not built" is what decides whether anybody needs to do something.`,
      ref: `${screen} · availability = unavailable`,
    }
  }
  return {
    t: 'note',
    tone: 'r',
    kicker: 'Availability',
    title: `${title} did not state whether it has figures`,
    body: 'Without that this console cannot tell "not computed" from "nothing happened", and it will not guess between them.',
    ref: `${screen} · availability not stated`,
  }
}

/**
 * The margin arithmetic on `rp-profit`, term by term.
 *
 * ALL FOUR TERMS render, including the ones with no figure. A list showing only
 * the measurable terms would show the cost side alone — and a screen listing
 * "provider costs" and nothing else reads as a customer who cost us money and
 * earned us none, which is a claim about the customer rather than about what we
 * record.
 */
function marginItems(raw: unknown): readonly KvItem[] | null {
  if (!Array.isArray(raw)) return null
  const items: KvItem[] = []
  for (const entry of raw as readonly unknown[]) {
    if (!isObject(entry)) continue
    const term = text(entry['term'])
    const availability = availabilityOf(entry['availability'])
    const reason = text(entry['reason'])
    const value = entry['value'] === undefined ? null : place(entry['value'])
    items.push([
      term === '' ? '— unnamed term' : term,
      value !== null && value.ok
        ? `${String(value.minor)} minor · ${value.currency}`
        : availability === 'available'
          ? '— reported available with no amount'
          : availability === 'not_built'
            ? 'not built'
            : availability === 'unavailable'
              ? 'could not be read'
              : '— availability not stated',
      availability === 'not_built' ? 'not built' : availability === 'unavailable' ? 'unreadable' : '',
      availability === 'available' ? 'n' : availability === 'not_built' ? 'n' : 'r',
      reason === '' ? 'No reason was given for this term.' : reason,
      '',
    ])
  }
  return items.length === 0 ? null : items
}

// ---------------------------------------------------------------------------
// Composition
// ---------------------------------------------------------------------------

export function composeReport(payload: Record<string, unknown>, readAt: Date): ScreenData {
  const screen = text(payload['screen'])
  const title = text(payload['title'])
  const requirement = text(payload['requirement'])
  const availability = availabilityOf(payload['availability'])
  const reason = text(payload['reason'])
  const rowCap = whole(payload['row_cap'])
  const prov = readProvenance(payload['provenance'])

  const blocks: Block[] = []

  // `rows` is ABSENT rather than an empty array on a screen with no figures,
  // precisely so a client cannot render "0 results" for "not computed". So the
  // absence of the key is read, not the length of an array.
  const hasRows = Array.isArray(payload['rows'])
  const lines: ReportLine[] = []
  let unreadable = 0
  if (hasRows) {
    for (const entry of payload['rows'] as readonly unknown[]) {
      const parsed = readLine(entry)
      if (parsed === null) unreadable += 1
      else lines.push(parsed)
    }
  }

  if (availability === 'available' && hasRows) {
    const table: TableBlock = {
      t: 'table',
      title: `${title === '' ? screen : title}${requirement === '' ? '' : ` · ${requirement}`}`,
      src: prov?.source === 'analytics' ? 'analytics' : 'ledger',
      ...(prov?.source === 'analytics' && prov.readAt !== '' ? { fresh: `read ${prov.readAt}` } : {}),
      facets: false,
      cols: LINE_COLS,
      rows: lines.map(lineRow),
      footer: `${String(lines.length)} line(s)${unreadable === 0 ? '' : ` · ${String(unreadable)} could not be read`}${rowCap === null ? '' : ` · the interactive cap is ${String(rowCap)} rows`}`,
      foot2:
        'A request above the row cap is REFUSED with an export affordance rather than truncated: a truncated report renders as a complete one and sums to less than the truth. Every amount carries its currency, and a side the backend did not send is absent rather than zero.',
    }
    blocks.push(table)
    if (lines.length === 0 && unreadable === 0) {
      blocks.push({
        t: 'note',
        tone: 'n',
        kicker: 'No lines',
        title: 'This screen has figures and returned none for the period',
        body: 'That is a period with no activity, and it is a different statement from a figure nobody has computed — which this screen would have said instead.',
        ref: `${screen} · availability = available, rows = []`,
      })
    }
  } else {
    blocks.push(absenceNote(screen, title === '' ? screen : title, availability, reason))
  }

  if (unreadable > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Partial read',
      title: `${String(unreadable)} line(s) could not be read`,
      body: 'Counted rather than dropped. A report shorter than the record sums to less than the truth, which is the same failure truncation causes and the reason the cap refuses rather than truncates.',
      ref: 'the shape of those lines is not one this console knows',
    })
  }

  const margin = marginItems(payload['margin_components'])
  if (margin !== null) {
    blocks.push({
      t: 'kv',
      title: 'Contribution margin, term by term',
      items: margin,
    })
    blocks.push({
      t: 'note',
      tone: 'n',
      kicker: 'Why there is no margin',
      title: 'All four terms are listed, including the ones with no figure',
      body: 'A list showing only the measurable terms would show the cost side alone — and a screen listing provider costs and nothing else reads as a customer who cost us money and earned us none, which is a claim about the customer rather than about what we record. Three of the four have no ledger figure because nothing posts a fee, nothing posts a storefront purchase, and a referral commission is posted nowhere until a partner is paid through the institution.',
      ref: 'every value movement is executed at the institution first and posted from the resulting event',
    })
  }

  blocks.push({ t: 'kv', title: 'Freshness', items: freshnessItems(prov) })

  if (prov?.source === 'analytics') {
    blocks.push({
      t: 'note',
      tone: 'v',
      kicker: 'Law 12',
      title: 'Analytics-sourced, badged, and never on a money path',
      body: 'The mirror is a derived copy and never a source of truth. Nothing on this screen may be used as a balance, an entitlement, a limit or an authorisation state — those are ledger-sourced only, and there is no component in this console that can render an analytics-sourced balance.',
      ref: prov.dataset === '' ? 'the mirror' : prov.dataset,
    })
  }

  const generatedAt = text(payload['generated_at'])
  const stamp = generatedAt === ''
    ? readAtStamp(readAt)
    : `${generatedAt.slice(0, 10)} ${generatedAt.slice(11, 16)} UTC`

  const source = `${prov?.dataset === '' || prov === null ? 'go-wallet · reporting' : prov.dataset} (GET /v1/admin${reportPathFor(screen)})`
  const authority = prov?.source === 'analytics' ? 'analytics mirror · never a source of truth' : 'operational'

  const missing: string[] = []
  if (availability !== 'available') missing.push(`every figure on this screen — ${availability ?? 'availability not stated'}`)
  if (prov === null) missing.push('where the figures came from')

  const provenance: DataSource =
    missing.length === 0
      ? { kind: 'live', source, authority, readAt: stamp }
      : { kind: 'partial', source, authority, readAt: stamp, missing }

  return { blocks, provenance, rowKeys: lines.map((l) => l.report) }
}

// ---------------------------------------------------------------------------
// The eight screens
// ---------------------------------------------------------------------------

/**
 * Screen id → path, mirroring `reportPaths` in `admin_reports.go`.
 *
 * A table rather than a derivation, for the reason the backend keeps one: the
 * screen id and the URL must be able to differ, and a rule that generated one
 * from the other would rename an endpoint the day somebody renamed a nav entry.
 */
export const REPORT_PATHS: Readonly<Record<string, string>> = {
  'rp-ops': '/reports/ops-metrics',
  'rp-tieout': '/reports/tie-out',
  'rp-funnel': '/reports/funnel',
  'rp-pl': '/reports/profit-and-loss',
  'rp-profit': '/reports/customer-profitability',
  'rp-cohorts': '/reports/cohorts',
  'rp-deflection': '/reports/deflection',
  'rp-exports': '/reports/exports',
}

function reportPathFor(screen: string): string {
  return REPORT_PATHS[screen] ?? '/reports'
}

function unreadableScreen(screen: string, reason: string, decisionRef: string | null): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Reporting',
      title: 'This report could not be read',
      body: `${reason} No figure below is known. Note that this is NOT the same as the screen having no figures: the backend answers 200 with a stated availability for that, precisely so an operator can tell "nobody has built this" from "nobody answered".`,
      ref: decisionRef === null
        ? `${screen} · the request did not complete`
        : `decision_ref ${decisionRef}`,
    }],
    provenance: { kind: 'unread', source: `go-wallet · reporting (GET /v1/admin${reportPathFor(screen)})`, reason },
  }
}

function reportLoader(screen: string): ScreenLoader {
  return async (api: ApiClient) => {
    let payload: unknown
    try {
      payload = await api.get<unknown>(reportPathFor(screen))
    } catch (error) {
      if (error instanceof ApiError) {
        return unreadableScreen(
          screen,
          `The endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
          error.decisionRef,
        )
      }
      if (error instanceof DOMException && error.name === 'AbortError') throw error
      return unreadableScreen(screen, 'The backend could not be reached.', null)
    }
    if (!isObject(payload)) {
      return unreadableScreen(screen, 'The endpoint returned a shape this console does not know.', null)
    }
    return composeReport(payload, new Date())
  }
}

export const loadReportOps = reportLoader('rp-ops')
export const loadReportTieOut = reportLoader('rp-tieout')
export const loadReportFunnel = reportLoader('rp-funnel')
export const loadReportPL = reportLoader('rp-pl')
export const loadReportProfit = reportLoader('rp-profit')
export const loadReportCohorts = reportLoader('rp-cohorts')
export const loadReportDeflection = reportLoader('rp-deflection')
export const loadReportExports = reportLoader('rp-exports')

function postureTone(posture: string): Tone {
  if (posture === 'fresh') return 'g'
  if (posture === 'degraded') return 'a'
  return 'r'
}

/**
 * How current these figures are, and whether anything has proved them right.
 *
 * # Why this panel survived the sweep and its neighbours did not
 *
 * It was titled "Where these figures came from" and it carried seven rows, each
 * with a paragraph under it explaining what a mirror is, what a watermark is
 * and why absent is not zero. The paragraphs are gone: they explained the
 * console to the operator rather than reporting anything, which is what the
 * sweep was for.
 *
 * The VALUES stay, because law 12 requires them. Two of them — the watermark
 * and the lag — answer a question the provenance chip cannot: the chip stamps
 * when this console read the mirror, and these stamp how current the mirror
 * itself is. A report read a second ago off a mirror three days behind is fresh
 * by the chip and stale in fact, and that gap is the entire reason law 12 names
 * freshness separately from source.
 *
 * Absence is still never a zero. A mirror that did not say how far behind it is
 * has not said it is current, so `— not stated` is toned as attention and never
 * rendered as `0s`.
 */
function freshnessItems(p: Provenance | null): readonly KvItem[] {
  if (p === null) {
    return [['provenance', '— not returned', 'unread', 'r', '', '']]
  }
  const analytics = p.source === 'analytics'
  return [
    [
      'source',
      p.source === '' ? '— not stated' : p.source,
      analytics ? 'ANALYTICS' : p.source === 'ledger' ? 'ledger' : 'unknown',
      analytics ? 'v' : p.source === 'ledger' ? 'n' : 'r',
      '',
      '',
    ],
    ['dataset', p.dataset === '' ? '— not stated' : p.dataset, '', 'n', '', 'm'],
    ['read at', p.readAt === '' ? '— not stamped' : p.readAt, '', 'n', '', 'm'],
    [
      'mirror holds data through',
      p.materializedThrough === '' ? '— no watermark' : p.materializedThrough,
      '',
      p.materializedThrough === '' ? 'a' : 'n',
      '',
      'm',
    ],
    [
      'behind by',
      p.lagSeconds === null ? '— not stated' : `${String(p.lagSeconds)}s`,
      '',
      p.lagSeconds === null || p.lagSeconds > 900 ? 'a' : 'g',
      '',
      '',
    ],
    [
      'posture',
      p.posture === '' ? '— not stated' : p.posture,
      p.posture === 'degraded' ? 'DEGRADED' : p.posture,
      postureTone(p.posture),
      '',
      '',
    ],
    ['tied out', p.tieOut === '' ? '— not stated' : p.tieOut, '', 'n', '', ''],
  ]
}
